import {Component, inject, input, OnInit, output, signal} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators} from '@angular/forms';
import {CampaignsApiService} from '@core/services/api/campaigns-api.service';
import {AlertService} from '@core/services/alert.service';
import {CpfCnpjPipe} from '@shared/pipes/app-cpfcnpj.pipe';
import {AppInputComponent} from '@shared/components/forms/app-input/app-input.component';

@Component({
    selector: 'app-step-2-preview',
    standalone: true,
    imports: [CommonModule, FormsModule, ReactiveFormsModule, CpfCnpjPipe, AppInputComponent],
    template: `
        <div class="step-content">
             <h4 class="mb-1">Preview e Seleção</h4>
            <p class="text-muted mb-4">Confira o resumo das empresas que atendem aos filtros aplicados.</p>

            @if (isLoading()) {
                <div class="text-center py-5">
                    <div class="spinner-border text-primary" role="status">
                        <span class="visually-hidden">Carregando...</span>
                    </div>
                    <p class="text-muted mt-3">Buscando empresas com base nos filtros...</p>
                </div>
            } @else {
                <!-- Resumo -->
                <div class="row g-3 mb-4">
                    <div class="col-md-6">
                        <div class="card card-body">
                            <div class="d-flex align-items-center">
                                <div class="avatar-sm flex-shrink-0">
                                    <span class="avatar-title bg-primary-subtle text-primary rounded-circle fs-3">
                                        <i class="ti ti-building"></i>
                                    </span>
                                </div>
                                <div class="flex-grow-1 ms-3">
                                    <p class="text-uppercase fw-medium text-muted mb-1">Total de Empresas</p>
                                    <h4 class="mb-0">{{ localPreview().totalCompanies | number }}</h4>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="col-md-6">
                        <div class="card card-body">
                             <div class="d-flex align-items-center">
                                <div class="avatar-sm flex-shrink-0">
                                    <span class="avatar-title bg-success-subtle text-success rounded-circle fs-3">
                                        <i class="ti ti-currency-real"></i>
                                    </span>
                                </div>
                                <div class="flex-grow-1 ms-3">
                                    <p class="text-uppercase fw-medium text-muted mb-1">Valor Total Estimado</p>
                                    <h4 class="mb-0">{{ localPreview().totalEstimatedValue | currency }}</h4>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Aviso sobre TOP 5 -->
                <div class="alert alert-info d-flex align-items-center mb-3">
                    <i class="ti ti-info-circle fs-5 me-2"></i>
                    <div class="small">
                        <strong>Preview de Empresas:</strong> Abaixo estão listadas as <strong>5 empresas com maiores dívidas</strong> que atendem aos filtros. 
                        Todas as <strong>{{ localPreview().totalCompanies | number }}</strong> empresas serão incluídas na campanha.
                    </div>
                </div>

                <!-- Campo maxCompanies -->
                <div class="card mb-3">
                    <div class="card-body">
                        <h6 class="mb-3">
                            <i class="ti ti-adjustments me-2"></i>
                            Limite de Empresas (Opcional)
                        </h6>
                        <p class="text-muted small mb-3">
                            Por padrão, todas as <strong>{{ localPreview().totalCompanies | number }}</strong> empresas encontradas serão incluídas. 
                            Se desejar limitar, informe abaixo a quantidade máxima.
                        </p>
                        
                        <form [formGroup]="form">
                            <div class="row">
                                <div class="col-md-6">
                                    <app-input
                                        formControlName="maxCompanies"
                                        type="number"
                                        label="Quantidade Máxima de Empresas"
                                        placeholder="Ex: 100"
                                        containerStyles="mb-0"
                                        [min]="1"
                                        [max]="localPreview().totalCompanies"
                                    />
                                </div>
                                <div class="col-md-6 d-flex align-items-end">
                                    <div class="alert alert-warning mb-0 w-100" style="padding: 0.5rem 0.75rem;">
                                        <small>
                                            <i class="ti ti-info-circle me-1"></i>
                                            @if (form.get('maxCompanies')?.value) {
                                                <strong>{{ form.get('maxCompanies')?.value | number }}</strong> empresas serão incluídas
                                            } @else {
                                                <strong>{{ localPreview().totalCompanies | number }}</strong> empresas serão incluídas
                                            }
                                        </small>
                                    </div>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>

                <!-- Lista de Empresas -->
                <h5 class="mb-3">Top 5 Empresas (Maiores Dívidas)</h5>
                <div class="card">
                    <div class="card-body p-0">
                        <div class="table-responsive">
                            <table class="table table-hover table-striped mb-0">
                                <thead class="table-light sticky-top">
                                    <tr>
                                        <th class="ps-3">Empresa</th>
                                        <th>Cidade/UF</th>
                                        <th class="text-end pe-3">Dívida Estimada</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    @for (company of localPreview().companies; track company.id) {
                                        <tr>
                                            <td class="ps-3">
                                                <div class="fw-medium">{{ company.name }}</div>
                                                <small class="text-muted">{{ company.documentNumber | cpfCnpj }}</small>
                                            </td>
                                            <td>
                                                {{ company.city }}{{ company.state ? '/' + company.state : '' }}
                                            </td>
                                            <td class="text-end pe-3">
                                                <strong class="text-success">{{ company.estimatedDebtValue | currency}}</strong>
                                            </td>
                                        </tr>
                                    } @empty {
                                        <tr>
                                            <td colspan="3" class="text-center py-5 text-muted">
                                                <i class="ti ti-mood-empty fs-1 d-block mb-2"></i>
                                                Nenhuma empresa encontrada com os filtros aplicados.
                                            </td>
                                        </tr>
                                    }
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                @if (localPreview().companies.length > 0) {
                    <div class="mt-3">
                        <small class="text-muted">
                            <i class="ti ti-info-circle me-1"></i>
                            Mostrando apenas as 5 empresas com maiores dívidas. Total de <strong>{{ localPreview().totalCompanies | number }}</strong> empresas serão incluídas na campanha.
                        </small>
                    </div>
                }
            }
        </div>
    `,
    styles: [`
        .step-content {
           /* O padding agora é controlado pelo container pai */
        }
        .sticky-top {
            position: sticky;
            top: 0;
            z-index: 10;
        }
        .avatar-sm {
            width: 48px;
            height: 48px;
            min-width: 48px;
            min-height: 48px;
        }
        .avatar-title {
            width: 100%;
            height: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
        }
    `]
})
export class Step2PreviewComponent implements OnInit {
    private campaignsApiService = inject(CampaignsApiService);
    private alertService = inject(AlertService);
    private fb = inject(FormBuilder);

    filters = input.required<any>();
    previewData = input.required<any>();
    campaignData = input.required<any>();
    dataChange = output<any>();
    campaignDataChange = output<any>();
    validityChange = output<boolean>();

    protected isLoading = signal(false);
    protected localPreview = signal({
        totalCompanies: 0,
        totalEstimatedValue: 0,
        companies: [] as any[]
    });
    protected form!: FormGroup;

    ngOnInit(): void {
        this.createForm();
        this.loadPreview();
    }

    private createForm(): void {
        this.form = this.fb.group({
            maxCompanies: [
                this.campaignData().maxCompanies || null,
                [Validators.min(1)]
            ]
        });

        // Emitir mudanças e validade
        this.form.valueChanges.subscribe(() => {
            this.emitChanges();
        });

        // Emitir validade inicial
        this.validityChange.emit(this.form.valid);
    }

    private emitChanges(): void {
        const maxCompanies = this.form.get('maxCompanies')?.value;
        this.campaignDataChange.emit({
            maxCompanies: maxCompanies || undefined
        });
        this.validityChange.emit(this.form.valid);
    }

    private loadPreview(): void {
        this.isLoading.set(true);

        this.campaignsApiService.preview(this.filters()).subscribe({
            next: (response: any) => {
                if (response.success) {
                    const preview = {
                        totalCompanies: response.data.totalCompanies || 0,
                        totalEstimatedValue: response.data.totalEstimatedValue || 0,
                        companies: response.data.companies || []
                    };
                    this.localPreview.set(preview);
                    this.dataChange.emit(preview);

                    // Atualizar validador de máximo baseado no total de empresas
                    this.form.get('maxCompanies')?.setValidators([
                        Validators.min(1),
                        Validators.max(preview.totalCompanies)
                    ]);
                    this.form.get('maxCompanies')?.updateValueAndValidity();
                    this.validityChange.emit(this.form.valid);
                }
                this.isLoading.set(false);
            },
            error: () => {
                this.alertService.error('Erro', 'Falha ao carregar preview das empresas');
                this.isLoading.set(false);
            }
        });
    }

}
